<template>
  <div
    :class="[
      ns.e('cell'),
      ns.e('header-cell'),
      ns.is('sortable', column.sortable),
      ns.is('sorted', !!sortOrder),
      ns.is('fixed-left', column.fixed === 'left'),
      ns.is('fixed-right', column.fixed === 'right'),
    ]"
    :data-column-id="column.id"
    :style="cellStyle"
  >
    <template v-if="column.type === 'checkbox'">
      <z-checkbox
        :model-value="reactData.isAllSelected"
        :indeterminate="reactData.isIndeterminate"
        :size="gridSize"
        @update:model-value="$grid.handleCheckedAllRow?.($event)"
        @click.stop
      />
    </template>
    <template v-else-if="column.type === 'seq'">
      <span
        ref="titleRef"
        :class="ns.e('header-title-text')"
        :title="titleTooltip"
        @mouseenter="onTitleMouseEnter"
      >
        {{ title }}
      </span>
    </template>
    <template v-else>
      <div :class="ns.e('header-title')">
        <span :class="[ns.e('header-title-side'), ns.em('header-title-side', 'start')]">
          <span v-if="showRequired" :class="ns.e('required-icon')">
            <i>*</i>
          </span>
          <span v-if="showEditable" :class="ns.e('edit-icon')">
            <z-icon :size="10">
              <Edit />
            </z-icon>
          </span>
        </span>
        <span
          ref="titleRef"
          :class="[
            ns.e('header-title-text'),
            ns.is('sortable', column.sortable),
            ns.is('filterable', showFilterIcon),
          ]"
          :title="titleTooltip"
          @mouseenter="onTitleMouseEnter"
          @click="onTitleClick"
        >
          {{ title }}
        </span>
        <span :class="[ns.e('header-title-side'), ns.em('header-title-side', 'end')]">
          <span
            v-if="showFilterIcon"
            :class="[
              ns.e('header-filter'),
              ns.is('active', hasColumnFilter),
            ]"
            @click.stop="onFilterClick"
          >
            <z-icon :size="14">
              <Filter />
            </z-icon>
          </span>
          <span v-if="column.sortable" :class="ns.e('sort')" @click.stop>
            <z-icon
              :class="[
                ns.e('sort-asc'),
                ns.is('active', sortOrder === 'asc'),
              ]"
              :color="sortOrder === 'asc' ? 'var(--z-color-primary)' : undefined"
              :size="14"
              @click.stop="emit('sort', 'asc')"
            >
              <CaretTop />
            </z-icon>
            <z-icon
              :class="[
                ns.e('sort-desc'),
                ns.is('active', sortOrder === 'desc'),
              ]"
              :color="sortOrder === 'desc' ? 'var(--z-color-primary)' : undefined"
              :size="14"
              @click.stop="emit('sort', 'desc')"
            >
              <CaretBottom />
            </z-icon>
          </span>
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { CaretBottom, CaretTop, Edit, Filter } from '@zoryn/icons'
import { useNamespace } from '@zoryn/hooks'
import ZCheckbox from '@zoryn/components/checkbox'
import ZIcon from '@zoryn/components/icon'
import { isGridColumnEditable, resolveGridEditable } from '../column/meta'
import { findFirstFieldCondition } from '../filter/filter-group'
import { isActiveFilter, isFilterableColumn } from '../filter/filter-utils'
import { useOverflowTooltip } from '../hooks/use-overflow-tooltip'
import type { CSSProperties } from 'vue'
import type { DataGrid, GridColumnInfo } from '../types'

const ns = useNamespace('grid')

const props = defineProps<{
  column: GridColumnInfo
  cellStyle?: CSSProperties
}>()

const emit = defineEmits<{
  sort: ['asc' | 'desc']
}>()

const $grid = inject('$grid') as DataGrid
const { reactData, options, internalData } = $grid
const gridSize = computed(() => $grid.gridSize.value)

const sortOrder = computed(() => {
  const { field } = props.column
  if (!field) {
    return props.column.order ?? null
  }
  return internalData.sortMap?.[field]?.order ?? props.column.order ?? null
})

const titleRef = ref<HTMLElement>()
const { overflowActive, onTextMouseEnter } = useOverflowTooltip()

const title = computed(() => props.column.title || props.column.field || '')
const titleTooltip = computed(() => (overflowActive.value ? title.value : undefined))
const tableEditable = computed(() => resolveGridEditable($grid.props, options))
const showEditable = computed(() =>
  isGridColumnEditable(props.column, tableEditable.value)
)
const showRequired = computed(() => showEditable.value && !!props.column.required)
const showFilterIcon = computed(() => isFilterableColumn(props.column))
const hasColumnFilter = computed(() => {
  const { field } = props.column
  if (!field) {
    return false
  }
  const condition = findFirstFieldCondition(reactData.filterGroups, field)
  return condition ? isActiveFilter(condition) : false
})

function onTitleMouseEnter(event: MouseEvent) {
  void onTextMouseEnter(event)
}

function onTitleClick(event: MouseEvent) {
  const { column } = props
  if (isFilterableColumn(column)) {
    $grid.openColumnMenu?.(
      column,
      titleRef.value ?? (event.currentTarget as HTMLElement)
    )
    return
  }
  if (!column.sortable || !column.field) {
    return
  }
  const current = sortOrder.value
  if (current === 'asc') {
    emit('sort', 'desc')
  } else if (current === 'desc') {
    emit('sort', 'desc')
  } else {
    emit('sort', 'asc')
  }
}

function onFilterClick(event: MouseEvent) {
  $grid.openColumnMenu?.(props.column, event.currentTarget as HTMLElement)
}
</script>

